import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Location } from 'history';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Prompt } from 'react-router-dom';
import styled from 'styled-components';

import { WoltModal } from 'common/components';

type Props = {
  when: boolean;
};

const StyledModal = styled(WoltModal)`
  max-width: 448px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  ${typographyCss.Body3};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

export const UploadInProgressConfirmationPrompt = ({ when }: Props) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [nextLocation, setNextLocation] = useState<Location>();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when && !nextLocation) {
        event.preventDefault();
        const message = intl.formatMessage({ id: 'menu.bulk-image.prompt-body' });
        event.returnValue = message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [intl, nextLocation, when]);

  const shouldGoToNextLocation = useCallback(
    (location: Location) => {
      if (!isOpen) {
        setNextLocation(location);
        setIsOpen(true);
        return false;
      }
      return true;
    },
    [isOpen],
  );

  const handleConfirm = useCallback(() => {
    if (nextLocation) {
      // Assign the new location to fully reload and cancel any pending async processes
      window.location.assign(nextLocation.pathname + nextLocation.search);
      setIsOpen(false);
    }
  }, [nextLocation]);

  const handleCancel = () => setIsOpen(false);

  return (
    <>
      <Prompt when={when} message={shouldGoToNextLocation} />
      <StyledModal isOpen={isOpen} onRequestClose={handleCancel}>
        <ModalHeader onCloseClick={handleCancel}>
          <FormattedMessage id="menu.bulk-image.upload-prompt-header" />
        </ModalHeader>
        <StyledModalContent>
          <FormattedMessage id="menu.bulk-image.upload-prompt-body" />
        </StyledModalContent>
        <StyledModalFooter>
          <Button variant="lightBlue" size="small" onClick={handleCancel}>
            <FormattedMessage id="menu.bulk-image.upload-prompt-cancel" />
          </Button>
          <Button variant="blue" size="small" onClick={handleConfirm} style={{ marginLeft: 16 }}>
            <FormattedMessage id="menu.bulk-image.upload-prompt-confirm" />
          </Button>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
};
